import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef
} from "react"
import { useCurrentUser } from "hooks/use-current-user"

export const PromoCodeContext = createContext()

// PromoCodeProvider is a context provider that sets and removes promo codes
// from url params and localStorage.
//
// If a user provides a promo_code in URL params, we'll set that promo code in
// local storage and make it available to app. This is used in places like payment
// forms and upgrade pages.
//
// Without a URL or stored code, an eligible account falls back to its Signals
// launch offer (window.app.user.signals_launch_offer). That code is never saved,
// so it can't outlive the offer; the server resolves it again on each page load.
export const PromoCodeProvider = ({ children, history = window.history }) => {
  const { signals_launch_offer: launchOffer, refreshSignalsLaunchOffer } =
    useCurrentUser()
  // Read on the first render, not in an effect, so anything derived from it
  // (such as hiding the launch offer for a different code) is right on first paint.
  const [explicitPromoCode, setExplicitPromoCode] = useState(
    () => urlPromoCode() || getItem("promo_code")
  )
  // In some cases, we may want to delay fetching data until the promo code
  // has been retrieved and set. Use this state to handle that case.
  const [promoCodeLoaded, setPromoCodeLoaded] = useState(false)
  const isFirstRender = useRef(true)

  useEffect(() => {
    const code = urlPromoCode()
    if (code) setItem("promo_code", code)

    setPromoCodeLoaded(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }
    try {
      if (!explicitPromoCode) {
        localStorage.removeItem("promo_code")
        const url = new URL(window.location.href)
        url.searchParams.delete("promo_code")
        history.replaceState({}, document.title, url.href)
      }
    } catch {
      // noop
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [explicitPromoCode])

  const launchOfferCode = launchOffer?.code
  // A campaign code the user brought (e.g. a trial link clicked by a paying
  // account) means they came for the launch offer, so the server launch offer code
  // replaces it since that's the one their account qualifies for. Any other code the
  // user brought takes precedence over the launch offer.
  const broughtPromoCode =
    launchOfferCode && isCampaignCode(explicitPromoCode, launchOffer)
      ? null
      : explicitPromoCode
  const promoCode = broughtPromoCode || launchOfferCode || null
  // Whether the launch offer code is the one that applies, rather than a code
  // the user brought. Consumers use it to display the offer while it
  // applies, and to skip auto-opening checkout or showing "cannot be applied"
  // for a code the user didn't bring.
  const isLaunchOfferApplied = !broughtPromoCode && Boolean(launchOfferCode)

  // For a code that's done with: dismissed, or just used to upgrade. Re-reads the
  // launch offer too, since an upgrade can change which one the account gets and
  // window.app.user only refreshes on a full page load.
  const removePromoCode = async () => {
    setExplicitPromoCode(null)

    try {
      await refreshSignalsLaunchOffer?.()
    } catch {
      // UserProvider has already cleared the offer rather than keep a stale one.
    }
  }

  return (
    <PromoCodeContext.Provider
      value={{
        promoCode,
        isLaunchOfferApplied,
        removePromoCode,
        promoCodeLoaded
      }}
    >
      {children}
    </PromoCodeContext.Provider>
  )
}

const usePromoCode = () => {
  const context = useContext(PromoCodeContext)
  if (!context)
    console.warn("usePromoCode must be used within a PromoCodeProvider")
  return context
}

// Utilities

// Promotion codes are matched case-insensitively on the server.
const isCampaignCode = (code, launchOffer) =>
  Boolean(code) &&
  (launchOffer?.campaign_codes || []).some(
    campaignCode => campaignCode.toUpperCase() === code.toUpperCase()
  )

const urlPromoCode = () => {
  try {
    return new URLSearchParams(window.location.search).get("promo_code")
  } catch (e) {
    console.warn("Error reading promo code", e)
    return null
  }
}

// Set an item in local storage with an expiry date of 9 days
// If the item is already set and the value is the same, do nothing
//
// Getting a promo code's actual expiry date would require additional
// network calls, so for now, 9 days will do the trick.
export const setItem = (key, value) => {
  try {
    if (getItem(key) === value) return

    const ttl = 777600000 // 9 days
    localStorage.setItem(
      key,
      JSON.stringify({ value, expiry: Date.now() + ttl })
    )
  } catch (e) {
    console.warn("Error setting promo code", e)
  }
}

export const getItem = key => {
  try {
    const item = JSON.parse(localStorage.getItem(key))
    if (item && Date.now() <= item.expiry) return item.value

    localStorage.removeItem(key)
  } catch (e) {
    console.warn("Error retrieving promo code", e)
  }
  return null
}

export { usePromoCode }
